import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import './Custos.css';

// Tela de Custos do Aliado (04/11/2026). Responde "quanto custa o Aliado por hora total gravada, por
// hora de cliente e por vendedor médio, no modo manual e no AWON" — as MESMAS medidas nos dois modos,
// para comparar banana com banana. A conta é da API (GET /admin/custos, custos_calc.py), e cada
// ambiente mostra só o próprio custo. A tela antiga (/custos-atendimento) fica como legado.

// Meta de custo: R$ 1,00 por hora total gravada, sobre o custo operacional (a mesma base da tela antiga).
const META_POR_HORA = 1;
const PERIODOS = [7, 30, 60];
const LS_TAXA = 'custos_usdBrl';

const MODOS = [
    { id: 'manual', nome: 'Manual', sub: 'gravação do app, só durante o atendimento' },
    { id: 'awon', nome: 'AWON', sub: 'microfone ligado o dia todo' },
];

// Ordem e cor das peças nas barras e na legenda.
const PECAS = [
    { id: 'transcricao', nome: 'Transcrição (ElevenLabs)', cor: '#4e79a7' },
    { id: 'llm', nome: 'LLM do n8n', cor: '#f28e2b' },
    { id: 'julgador', nome: 'Julgador', cor: '#e15759' },
    { id: 'worker2', nome: 'worker2', cor: '#76b7b2' },
    { id: 'worker1', nome: 'worker1', cor: '#59a14f' },
    { id: 'maquinas', nome: 'Máquinas do AWON', cor: '#edc948' },
    { id: 'infra_uso', nome: 'Infra por uso', cor: '#b07aa1' },
    { id: 'infra_fixa', nome: 'Capacidade (infra fixa)', cor: '#bab0ac' },
];
const PECAS_OPERACIONAIS = PECAS.filter((p) => p.id !== 'infra_fixa');

const lsGet = () => { try { return localStorage.getItem(LS_TAXA) || ''; } catch { return ''; } };
const lsSet = (v) => {
    try { if (v) localStorage.setItem(LS_TAXA, v); else localStorage.removeItem(LS_TAXA); } catch { /* storage bloqueado */ }
};

const num = (v, dec = 0) =>
    v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const pct = (v) => (v == null ? '—' : `${num(v * 100, 0)}%`);
const brlDe = (taxa) => (v, dec = 2) =>
    v == null || !taxa ? '—' : `R$ ${(v * taxa).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const dataBr = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}` : iso;
};
const isoDe = (d) => d.toISOString().slice(0, 10);
const ontemBrt = () => {
    const agora = new Date(Date.now() - 3 * 3600 * 1000);   // relógio de Brasília (sem horário de verão)
    agora.setUTCDate(agora.getUTCDate() - 1);
    return agora;
};
const periodoDe = (dias) => {
    const fim = ontemBrt();
    const ini = new Date(fim);
    ini.setUTCDate(ini.getUTCDate() - (dias - 1));
    return { ini: isoDe(ini), fim: isoDe(fim) };
};

// ── Gráfico de linhas simples (SVG): uma linha por modo ────────────────────────────────────────────
function GraficoLinhas({ pontos, taxa, formatar }) {
    const largura = 720;
    const altura = 220;
    const margem = { e: 56, d: 12, t: 12, b: 28 };
    const dias = [...new Set(pontos.map((p) => p.dia))].sort();
    const valores = pontos.map((p) => p.valor).filter((v) => v != null && Number.isFinite(v));
    if (!dias.length || !valores.length || !taxa) return <p className="cu-vazio">Sem dados no período.</p>;
    const maximo = Math.max(...valores) * taxa * 1.1 || 1;
    const x = (i) => margem.e + (dias.length === 1 ? 0.5 : i / (dias.length - 1)) * (largura - margem.e - margem.d);
    const y = (v) => altura - margem.b - (v * taxa / maximo) * (altura - margem.t - margem.b);
    const marcas = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maximo / taxa);
    const passo = Math.max(1, Math.ceil(dias.length / 10));
    return (
        <svg className="cu-grafico" viewBox={`0 0 ${largura} ${altura}`} role="img" aria-label="Tendência do custo por hora">
            {marcas.map((m) => (
                <g key={m}>
                    <line x1={margem.e} x2={largura - margem.d} y1={y(m)} y2={y(m)} className="cu-grade" />
                    <text x={margem.e - 6} y={y(m) + 4} textAnchor="end" className="cu-eixo">{formatar(m)}</text>
                </g>
            ))}
            {dias.map((d, i) => (i % passo === 0 ? (
                <text key={d} x={x(i)} y={altura - 8} textAnchor="middle" className="cu-eixo">{dataBr(d)}</text>
            ) : null))}
            {MODOS.map((m) => {
                const serie = dias.map((d, i) => {
                    const p = pontos.find((q) => q.dia === d && q.modo === m.id);
                    return p && p.valor != null && Number.isFinite(p.valor) ? [x(i), y(p.valor), p] : null;
                }).filter(Boolean);
                if (!serie.length) return null;
                return (
                    <g key={m.id} className={`cu-linha cu-linha-${m.id}`}>
                        <polyline points={serie.map(([a, b]) => `${a},${b}`).join(' ')} fill="none" />
                        {serie.map(([a, b, p]) => (
                            <circle key={p.dia} cx={a} cy={b} r="3">
                                <title>{`${m.nome} ${dataBr(p.dia)}: ${formatar(p.valor)} — ${num(p.horas, 1)} h`}</title>
                            </circle>
                        ))}
                    </g>
                );
            })}
        </svg>
    );
}

// ── Barra empilhada de composição (uma por modo) ───────────────────────────────────────────────────
function BarraComposicao({ pecas, horas, base, brl }) {
    const lista = (base === 'total' ? PECAS : PECAS_OPERACIONAIS)
        .map((p) => ({ ...p, v: pecas?.[p.id] }))
        .filter((p) => p.v != null && p.v > 0);
    const soma = lista.reduce((a, p) => a + p.v, 0);
    if (!soma || !horas) return <p className="cu-vazio">Sem custo no período.</p>;
    return (
        <div>
            <div className="cu-barra" role="img" aria-label="Composição do custo">
                {lista.map((p) => (
                    <span key={p.id} style={{ width: `${(p.v / soma) * 100}%`, background: p.cor }}
                        title={`${p.nome}: ${brl(p.v / horas)} por hora gravada (${num((p.v / soma) * 100, 0)}%)`} />
                ))}
            </div>
            <ul className="cu-legenda">
                {lista.map((p) => (
                    <li key={p.id}>
                        <i style={{ background: p.cor }} />
                        <span>{p.nome}</span>
                        <b>{brl(p.v / horas)}/h</b>
                        <em>{num((p.v / soma) * 100, 0)}%</em>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Custos() {
    const [dias, setDias] = useState(7);
    const [faixa, setFaixa] = useState(periodoDe(7));     // o período consultado
    const [rascunho, setRascunho] = useState(periodoDe(7)); // as datas nos campos, até clicar em Atualizar
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [base, setBase] = useState('operacional');     // operacional | total
    const [unidadeGrafico, setUnidadeGrafico] = useState('hora_gravada');
    const [aberto, setAberto] = useState(null);           // `${subdomain}-${modo}` expandido
    const [campoTaxa, setCampoTaxa] = useState(lsGet);

    const carregar = useCallback(async (ini, fim) => {
        setCarregando(true);
        setErro('');
        try {
            setDados(await apiService.adminGetCustos(ini, fim));
        } catch (e) {
            setErro(e.message || 'Erro ao buscar os custos');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(faixa.ini, faixa.fim); }, [faixa, carregar]);

    const cotacao = dados?.usd_brl || null;
    const adotouCotacao = useRef(false);
    useEffect(() => {
        if (adotouCotacao.current || campoTaxa || !cotacao?.rate) return;
        adotouCotacao.current = true;
        setCampoTaxa(String(cotacao.rate));
    }, [cotacao, campoTaxa]);
    const taxa = (() => {
        const n = parseFloat(String(campoTaxa).replace(',', '.'));
        return Number.isFinite(n) && n > 0 ? n : null;
    })();
    const brl = brlDe(taxa);

    const escolherDias = (d) => { const f = periodoDe(d); setDias(d); setRascunho(f); setFaixa(f); };
    const aplicarDatas = () => {
        if (!rascunho.ini || !rascunho.fim || rascunho.ini > rascunho.fim) return;
        setDias(null);
        if (rascunho.ini === faixa.ini && rascunho.fim === faixa.fim) carregar(faixa.ini, faixa.fim);
        else setFaixa({ ...rascunho });
    };
    const unid = (m) => m?.[base] || {};

    const pontosGrafico = (dados?.serie || []).map((s) => {
        const custo = base === 'total' ? s.total_usd : s.operacional_usd;
        const horas = unidadeGrafico === 'hora_cliente' ? s.horas_cliente : s.horas_gravadas;
        return { dia: s.dia, modo: s.modo, horas, valor: horas > 0 ? custo / horas : null };
    });
    const totalLlm = (dados?.llm_por_no || []).reduce((a, n) => a + n.usd, 0);

    return (
        <div className="cu-page">
            <h1 className="cu-title">
                💰 Custos do Aliado
                {dados?.ambiente && <span className={`cu-amb cu-amb-${dados.ambiente}`}>{dados.ambiente.toUpperCase()}</span>}
            </h1>
            <p className="cu-help">
                As mesmas três medidas nos dois modos: <strong>hora total gravada</strong> (tudo o que foi gravado e
                processado), <strong>hora de cliente</strong> (a parte que é atendimento, pela classificação do n8n) e{' '}
                <strong>vendedor médio</strong> (o real gravado, sem jornada). Custo <strong>operacional</strong> é o que
                varia com o uso; o <strong>cheio</strong> soma a capacidade fixa da infra. Retrabalho fica à parte.
                Só o custo deste ambiente.
            </p>

            <div className="cu-filtros">
                <span className="cu-rotulo">Período</span>
                {PERIODOS.map((d) => (
                    <button key={d} className={`cu-botao ${dias === d ? 'cu-ativo' : ''}`} onClick={() => escolherDias(d)}>
                        {d} dias
                    </button>
                ))}
                <input type="date" className="cu-data" value={rascunho.ini} max={rascunho.fim}
                    onChange={(e) => setRascunho({ ...rascunho, ini: e.target.value })} aria-label="Data inicial" />
                <span className="cu-ate">até</span>
                <input type="date" className="cu-data" value={rascunho.fim} min={rascunho.ini}
                    onChange={(e) => setRascunho({ ...rascunho, fim: e.target.value })} aria-label="Data final" />
                <button className="cu-botao" onClick={aplicarDatas}>Atualizar</button>
                <span className="cu-sep" />
                <span className="cu-rotulo">Custo</span>
                <button className={`cu-botao ${base === 'operacional' ? 'cu-ativo' : ''}`} onClick={() => setBase('operacional')}>Operacional</button>
                <button className={`cu-botao ${base === 'total' ? 'cu-ativo' : ''}`} onClick={() => setBase('total')}>Cheio</button>
                <span className="cu-sep" />
                <span className="cu-rotulo">Dólar</span>
                <span className="cu-cambio">
                    R$
                    <input type="text" inputMode="decimal" value={campoTaxa} placeholder="5,00"
                        onChange={(e) => { setCampoTaxa(e.target.value); lsSet(e.target.value); }} aria-label="Cotação do dólar em reais" />
                </span>
                {cotacao?.rate && taxa !== cotacao.rate && (
                    <button className="cu-botao" onClick={() => { lsSet(''); setCampoTaxa(String(cotacao.rate)); }}>
                        usar {num(cotacao.rate, 4)}
                    </button>
                )}
            </div>

            {erro && <div className="cu-erro">{erro}</div>}
            {carregando && <Loading />}
            {!carregando && dados && (
                <>
                    {!dados.aws?.disponivel && (
                        <div className="cu-aviso">
                            <strong>Sem a AWS neste ambiente.</strong> O Cost Explorer não respondeu
                            ({dados.aws?.erro || 'sem acesso'}): máquinas do AWON e infra compartilhada estão fora da conta.
                        </div>
                    )}

                    <div className="cu-modos">
                        {MODOS.map((m) => {
                            const d = dados.modos?.[m.id];
                            const u = unid(d);
                            const opHora = d?.operacional?.por_hora_gravada;
                            const vazio = !d || !d.horas_gravadas;
                            return (
                                <div key={m.id} className={`cu-modo cu-modo-${m.id}`}>
                                    <div className="cu-modo-cab">
                                        <span className="cu-modo-nome">{m.nome}</span>
                                        <span className="cu-modo-sub">{m.sub}</span>
                                    </div>
                                    {vazio ? <p className="cu-vazio">Sem gravação no período.</p> : (
                                        <>
                                            <div className="cu-destaque">
                                                <span className="cu-valor">{brl(u.por_hora_gravada)}</span>
                                                <span className="cu-por">por hora gravada</span>
                                                {opHora != null && taxa && (
                                                    <span className={`cu-meta ${opHora * taxa <= META_POR_HORA ? 'cu-meta-ok' : ''}`}>
                                                        operacional {num(opHora * taxa / META_POR_HORA, 1)}× a meta de R$ {num(META_POR_HORA, 2)}
                                                    </span>
                                                )}
                                            </div>
                                            <dl className="cu-medidas">
                                                <div><dt>por hora de cliente</dt><dd>{brl(u.por_hora_cliente)}</dd></div>
                                                <div><dt>por vendedor-mês</dt><dd>{brl(u.por_vendedor_mes, 0)}</dd></div>
                                                <div><dt>por vendedor-dia</dt><dd>{brl(u.por_vendedor_dia)}</dd></div>
                                                <div><dt>por atendimento</dt><dd>{brl(u.por_atendimento)}</dd></div>
                                                <div><dt>horas gravadas por vendedor-dia</dt><dd>{num(d.horas_por_vendedor_dia, 1)} h</dd></div>
                                                <div><dt>parte que é cliente</dt><dd>{pct(d.pct_cliente)}</dd></div>
                                            </dl>
                                            <p className="cu-rodape-modo">
                                                {num(d.horas_gravadas, 1)} h gravadas · {num(d.horas_cliente, 1)} h de cliente ·{' '}
                                                {num(d.vendedores)} vendedores · {num(d.atendimentos)} atendimentos ·{' '}
                                                <strong>{brl(base === 'total' ? d.total_usd : d.operacional_usd)}</strong> no período
                                            </p>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <h2 className="cu-sub">Por cliente</h2>
                    <div className="cu-tabela-wrap">
                        <table className="cu-tabela">
                            <thead>
                                <tr>
                                    <th>Cliente</th><th>Modo</th><th>Vendedores</th><th>h gravadas</th><th>h de cliente</th>
                                    <th>h / vendedor-dia</th><th>por hora gravada</th><th>por hora de cliente</th>
                                    <th>por vendedor-mês</th><th>no período</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dados.clientes || []).filter((c) => c.horas_gravadas > 0 || c.total_usd > 0).map((c) => {
                                    const k = `${c.subdomain}-${c.modo}`;
                                    const u = unid(c);
                                    return (
                                        <React.Fragment key={k}>
                                            <tr className="cu-clicavel" onClick={() => setAberto(aberto === k ? null : k)}>
                                                <td>{aberto === k ? '▾' : '▸'} {c.subdomain}</td>
                                                <td><span className={`cu-tag cu-tag-${c.modo}`}>{c.modo === 'awon' ? 'AWON' : 'Manual'}</span></td>
                                                <td>{num(c.vendedores)}</td>
                                                <td>{num(c.horas_gravadas, 1)}</td>
                                                <td>{num(c.horas_cliente, 1)} <em>({pct(c.pct_cliente)})</em></td>
                                                <td>{num(c.horas_por_vendedor_dia, 1)}</td>
                                                <td><strong>{brl(u.por_hora_gravada)}</strong></td>
                                                <td>{brl(u.por_hora_cliente)}</td>
                                                <td>{brl(u.por_vendedor_mes, 0)}</td>
                                                <td>{brl(base === 'total' ? c.total_usd : c.operacional_usd)}</td>
                                            </tr>
                                            {aberto === k && (
                                                <tr className="cu-detalhe">
                                                    <td colSpan={10}>
                                                        <BarraComposicao pecas={c.pecas} horas={c.horas_gravadas} base={base} brl={brl} />
                                                        {c.pecas?.worker1 === null && (
                                                            <p className="cu-nota">worker1: sem registro de tokens (fora da conta).</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h2 className="cu-sub">De onde vem o custo — por hora gravada</h2>
                    <div className="cu-composicao">
                        {MODOS.map((m) => (
                            <div key={m.id} className="cu-comp-modo">
                                <h3>{m.nome}</h3>
                                <BarraComposicao pecas={dados.modos?.[m.id]?.pecas} horas={dados.modos?.[m.id]?.horas_gravadas}
                                    base={base} brl={brl} />
                            </div>
                        ))}
                    </div>

                    <div className="cu-cab-grafico">
                        <h2 className="cu-sub">Tendência por dia</h2>
                        <div>
                            <button className={`cu-botao ${unidadeGrafico === 'hora_gravada' ? 'cu-ativo' : ''}`}
                                onClick={() => setUnidadeGrafico('hora_gravada')}>por hora gravada</button>
                            <button className={`cu-botao ${unidadeGrafico === 'hora_cliente' ? 'cu-ativo' : ''}`}
                                onClick={() => setUnidadeGrafico('hora_cliente')}>por hora de cliente</button>
                        </div>
                    </div>
                    <div className="cu-legenda-linhas">
                        {MODOS.map((m) => <span key={m.id} className={`cu-ll cu-ll-${m.id}`}>{m.nome}</span>)}
                        <span className="cu-nota">Dia com poucas horas gravadas (domingo) tende a pico: a infra do dia se divide por pouco.</span>
                    </div>
                    <GraficoLinhas pontos={pontosGrafico} taxa={taxa} formatar={(v) => brl(v)} />

                    <h2 className="cu-sub">LLM por etapa e modelo</h2>
                    <div className="cu-tabela-wrap">
                        <table className="cu-tabela">
                            <thead><tr><th>Modo</th><th>Etapa</th><th>Modelo</th><th>no período</th><th>% do LLM</th><th>por hora gravada do modo</th></tr></thead>
                            <tbody>
                                {(dados.llm_por_no || []).slice(0, 40).map((n) => {
                                    const h = dados.modos?.[n.modo]?.horas_gravadas;
                                    return (
                                        <tr key={`${n.modo}-${n.etapa}-${n.modelo}`}>
                                            <td><span className={`cu-tag cu-tag-${n.modo}`}>{n.modo === 'awon' ? 'AWON' : 'Manual'}</span></td>
                                            <td>{n.etapa}</td>
                                            <td><code>{n.modelo}</code></td>
                                            <td>{brl(n.usd)}</td>
                                            <td>{totalLlm ? `${num((n.usd / totalLlm) * 100, 1)}%` : '—'}</td>
                                            <td>{h ? brl(n.usd / h) : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="cu-blocos">
                        <div className="cu-bloco">
                            <h2 className="cu-sub">AWS deste ambiente</h2>
                            {dados.aws?.disponivel ? (
                                <ul className="cu-lista">
                                    <li><b>{brl(dados.aws.infra_uso_usd)}</b> infra por uso (entra no operacional)</li>
                                    <li><b>{brl(dados.aws.infra_fixa_usd)}</b> capacidade: hora de instância, banco, balanceador, IP e storage (só no cheio)</li>
                                    <li><b>{brl(dados.aws.maquinas_awon_usd)}</b> máquinas do AWON
                                        {dados.aws.maquinas_fonte?.estimado?.length ? ` — ${dados.aws.maquinas_fonte.estimado.length} dia(s) estimado(s) antes das tags` : ''}</li>
                                    {dados.aws.nao_atribuido_usd && (dados.aws.nao_atribuido_usd.infra_uso + dados.aws.nao_atribuido_usd.infra_fixa + dados.aws.nao_atribuido_usd.maquinas) > 0 && (
                                        <li><b>{brl(dados.aws.nao_atribuido_usd.infra_uso + dados.aws.nao_atribuido_usd.infra_fixa + dados.aws.nao_atribuido_usd.maquinas)}</b>{' '}
                                            em dias sem gravação: não atribuído a ninguém</li>
                                    )}
                                </ul>
                            ) : <p className="cu-vazio">Indisponível neste ambiente.</p>}
                            <p className="cu-nota">Infra rateada pela hora total gravada do dia, dos dois modos; máquinas do AWON só entre os clientes AWON.</p>
                        </div>
                        <div className="cu-bloco">
                            <h2 className="cu-sub">Retrabalho — fora das medidas</h2>
                            <p><b>{brl(dados.retrabalho?.total_usd)}</b> no período: rodadas do julgador desfeitas, análises e janelas apagadas por reprocessamento.</p>
                            {(dados.retrabalho?.itens || []).length > 0 && (
                                <ul className="cu-lista">
                                    {dados.retrabalho.itens.map((i) => (
                                        <li key={`${i.subdomain}-${i.modo}-${i.peca}`}>
                                            <b>{brl(i.usd)}</b> {i.subdomain} · {i.modo === 'awon' ? 'AWON' : 'manual'} · {i.peca}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="cu-bloco cu-lacunas">
                        <h2 className="cu-sub">Premissas e lacunas</h2>
                        <ul className="cu-lista">
                            {dados.tarifas && (
                                <li>Transcrição manual: US$ {num(dados.tarifas.elevenlabs_base_usd_hora, 2)}/h + {num(dados.tarifas.elevenlabs_entidades_usd_hora, 2)} PII
                                    {' '}+ {num(dados.tarifas.elevenlabs_keyterms_usd_hora, 2)} keyterms (quando o cliente usa). AWON: US$ {num(dados.tarifas.awon_usd_hora, 2)}/h só nos blocos com fala;
                                    o recorte que vai ao n8n reaproveita essa transcrição.</li>
                            )}
                            {(dados.lacunas || []).map((l) => <li key={l}>{l}</li>)}
                            <li>Período {dataBr(dados.ini)} a {dataBr(dados.fim)} ({dados.dias} dias, horário de Brasília). Vendedor-mês = custo ÷ vendedores ativos × 30 ÷ dias.
                                Câmbio {cotacao?.source ? `${cotacao.source.split(' ')[0]} ` : ''}{taxa ? num(taxa, 4) : '—'}.</li>
                        </ul>
                    </div>

                    <p className="cu-legado">
                        <Link to="/custos-atendimento">Tela antiga de Custos de Atendimento (legado)</Link>
                    </p>
                </>
            )}
        </div>
    );
}

export default Custos;
